'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  AddressAutocomplete,
  type AddressAutocompleteProps,
  type Suggestion,
} from '@verifime/components';
import type { TAddress } from '@verifime/utils';
import { addressApi, addressHooksApi } from '@verifime/address-api-definition';
import { mapPlaceToAddress } from './mapPlaceToAddress';

// Debounce the query before hitting VAS. 400ms is the longest pause that
// still feels responsive to users who pause mid-address; beyond that the
// UI feels laggy, below it we burn API calls on mid-typing keystrokes.
// Inside a cache window so the wait rarely compounds across keystrokes.
const DEBOUNCE_MS = 400;
// Minimum chars before triggering a request. 3 is the industry norm for
// paid address autocomplete (Google Places, Smarty, Postcoder, HERE,
// Azure Maps). Going higher (4+) breaks the common AU pattern of
// "1 Q" / "42 H" (number + short street prefix) where the user expects
// instant completions. Going lower (1-2) produces noisy matches and
// wastes paid calls.
const MIN_QUERY_CHARS = 3;

type AddressAutocompleteViewProps = {
  value: Partial<TAddress>;
  onChange: (value: Partial<TAddress>) => void;
  countryCode: string;
  label?: string;
  placeholder?: string;
};

type ErrorKind = 'unavailable' | 'throttled' | 'expired';

const ERROR_MESSAGES: Record<ErrorKind, string> = {
  unavailable: 'Autocomplete unavailable. Please enter your address manually.',
  throttled: 'Too many requests. Please try again in a moment.',
  expired: 'This suggestion expired. Please search again.',
};

export function AddressAutocompleteView(props: AddressAutocompleteViewProps) {
  const { value, onChange, countryCode, label, placeholder } = props;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isPlaceLoading, setIsPlaceLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Clear state on country change
  useEffect(() => {
    setQuery('');
    setDebouncedQuery('');
    setSuggestions([]);
    setHasQueried(false);
    setErrorKind(null);
  }, [countryCode]);

  const trimmedQuery = debouncedQuery.trim();

  const autocompleteQuery = addressHooksApi.useAutocompleteAddress(
    {
      queries: {
        query: trimmedQuery,
        countryHint: countryCode,
        maxResults: 5,
      },
    },
    {
      enabled: trimmedQuery.length >= MIN_QUERY_CHARS,
      retry: false,
      onSuccess: (data) => {
        const next: Suggestion[] = (data.suggestions ?? [])
          .filter((s) => typeof s.label === 'string' && typeof s.placeId === 'string')
          .map((s) => ({ label: s.label as string, placeId: s.placeId as string }));
        setSuggestions(next);
        setHasQueried(true);
        setErrorKind(null);
      },
      onError: (err: unknown) => {
        setSuggestions([]);
        setHasQueried(true);
        const status =
          (err as { response?: { status?: number } } | undefined)?.response?.status;
        if (status === 429) {
          setErrorKind('throttled');
        } else {
          setErrorKind('unavailable');
        }
      },
    },
  );

  const handleSuggestionSelect = useCallback(
    async (placeId: string) => {
      setIsPlaceLoading(true);
      try {
        const place = await addressApi.getPlaceById({ params: { placeId } });
        onChange(mapPlaceToAddress(place, countryCode));
        setErrorKind(null);
      } catch (err: unknown) {
        const status =
          (err as { response?: { status?: number } } | undefined)?.response?.status;
        if (status === 404) {
          setErrorKind('expired');
          setSuggestions([]);
          setQuery('');
          setDebouncedQuery('');
        } else {
          setErrorKind('unavailable');
        }
      } finally {
        setIsPlaceLoading(false);
      }
    },
    [countryCode, onChange],
  );

  const errorHint = errorKind ? ERROR_MESSAGES[errorKind] : undefined;

  const pureProps: AddressAutocompleteProps = {
    value,
    onChange,
    suggestions,
    isLoading: isPlaceLoading || autocompleteQuery.isFetching,
    hasQueried,
    errorHint,
    onQueryChange: setQuery,
    onSuggestionSelect: handleSuggestionSelect,
    title: label,
    placeholder,
    query,
  };

  return <AddressAutocomplete {...pureProps} />;
}

export default AddressAutocompleteView;
